import { Button, Col, DatePicker, Flex, Input, Modal, Row, Select, Table, TableProps } from "antd";
import Card from "antd/es/card/Card";
import { SorterResult, TableRowSelection } from "antd/es/table/interface";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import React, { useEffect, useState } from "react";
import { LoaderSpinner } from '../../../components/common/Loader';
import { getCobDenialImportTrips, getPDRCaseImportLookups, saveCobDenialImportTrips } from "../../../service/httpsCalls";
import "../billing.scss";
import { createTableConfig } from "../tableConfig";

const { Option } = Select;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

interface Lookup {
    id: number,
    name: string,
    value: string
}
interface PaginationObject {
    totalPages: number,
    loading: boolean,
    pageSize: number,
    currentPage: number,
    sortInfo: SorterResult<any>
}
export default function CobDenialImportTripLegs({ clientList, invoiceInfo, parentCallback }: any) {
    const [tableHeight, setTableHeight] = React.useState(300); // Adjust as needed
    const [client, setClient] = useState('10');
    const [invoiceNum, setInvoiceNum] = useState(invoiceInfo.invoiceNumber);
    const [searchByType, setSearchByType] = useState<any>(null);
    const [searchTypeOptions, setSearchTypeOptions] = useState<Lookup[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [records, setRecords] = useState<any>([]);
    const [selectedTripLegs, setSelectedTripLegs] = useState<React.Key[]>([]);
    const [legsLoader, setLegsLoader] = useState(false);
    const tblRef: Parameters<typeof Table>[0]['ref'] = React.useRef(null);
    const [tableKey, setTableKey] = useState(0); // Key state to refresh table
    const [tableColumns, setTableColumns] = useState([]);
    const [ startDate, setStartDate ] = useState<string | any>('');
    const [ endDate, setEndDate ] = useState<string | any>('');
   

    const [menuVisible, setMenuVisible] = useState(false);
    let paginationObject: PaginationObject = {
        totalPages: 0,
        loading: false,
        pageSize: 5,
        currentPage: 1,
        sortInfo: {}
    }

    const [tablePagination, setTablePagination] = useState(paginationObject);
    const [filtersState, setFiltersState] = useState({});
    const [filteredRowCount, setFilteredRowCount] = useState(0);
    const tableChanges: TableProps<any>['onChange'] = (pagination, filters, sorter, extra) => {
        if (extra.action == 'filter') {
            setFilteredRowCount(extra.currentDataSource.length);
            setFiltersState(filters);
            return;
        }
        if (extra.action == 'sort') {
            return;
        }
        setTablePagination({
            ...tablePagination,
            currentPage: pagination.current ?? 1,
            pageSize: pagination.pageSize ?? 10,
            totalPages: pagination.total ?? 100,
            sortInfo: sorter as SorterResult<any>
        })
    }

    useEffect(() => {
        if (submitted && tablePagination.pageSize != 0)
            handleSearch();
    }, [tablePagination.currentPage, tablePagination.pageSize, tablePagination.sortInfo]);

    useEffect(() => {
        getLookupData();
    }, []);

    useEffect(() => {
        if (records?.length && tableHeight == 300) {
            updateTableHeight();
        }
    }, [records])

    const updateTableHeight = () => {
        const updateHeight = () => {
            const container = document.getElementById("table-container");
            if (container) {
                const header = document.getElementById("table-container")?.getElementsByClassName("ant-table-header")[0].clientHeight ?? 10;
                setTableHeight(container.clientHeight - header); // Ensure it fits inside
            }
        };

        window.addEventListener("resize", updateHeight);
        updateHeight(); // Initial height calculation

        return () => window.removeEventListener("resize", updateHeight);
    }

    const handleSearch = () => {
        setSubmitted(true);
        setSelectedTripLegs([]);
        if (!(client && invoiceNum && searchByType && ((searchByType == 1 && (startDate || endDate)) || (searchByType == 2 && searchText)))) {
            return;
        }
        setTableColumns([]);
        setRecords([]);
        setTablePagination({
            ...tablePagination,
            loading: true
        });
        setFilteredRowCount(0);
        getCobDenialImportTrips({
            clientId: client,
            CobdenialInvoiceSId: invoiceInfo.id,
            searchByType: searchByType,
            tripCsv: searchByType == 1 ? [ startDate , endDate ].map((item) => dayjs(item).format('YYYY-MM-DD')).join(',') : searchText,
        }, (res: any) => {
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!';
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                })
                setRecords([]);
                setTablePagination({
                    ...tablePagination,
                    loading: false,
                    totalPages: 1
                });
            } else {
                setTablePagination({
                    ...tablePagination,
                    loading: false,
                    totalPages: Math.ceil(res.totalRecords / (tablePagination.pageSize / tablePagination.pageSize))
                });
                if (res.columnConfigs?.length) {
                    let cols = createTableConfig(res.columnConfigs);
                    setTableColumns(cols);
                }
                setRecords(res.items);
                let width = res.items?.length > 0 ? 1250 : 1000
                parentCallback({
                    updateDrawerConfig: true,
                    width: width
                });
            }
        })
    };

    const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // Regular expression to allow alphanumeric, [, ], and -
        const filteredValue = inputValue.replace(/[^a-zA-Z0-9,\-\s]/g, "");

        setSearchText(filteredValue);
    };

    const getLookupData = () => {
        getPDRCaseImportLookups(client, (res: any) => {
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!';
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                })
            } else {
                setSearchTypeOptions(res.searchByType);
                setSearchByType(res.searchByType[0].value);
            }
        })
    }


    const onSelectChange = (newSelectedTripKeys: React.Key[], selectedRows: any[]) => {
        setSelectedTripLegs(newSelectedTripKeys);
    };

    const rowSelection: TableRowSelection<any> = {
        selectedRowKeys: selectedTripLegs,
        onChange: debounce(onSelectChange, 300),
        fixed: 'left',
        getCheckboxProps: (record) => ({
            disabled: !record.selectable // Disable based on condition
        })
    };

    function getImportObj() {
        let tripLegData: any = [];
        selectedTripLegs.forEach((x) => {
            let index = records.findIndex((rec: any) => rec.uniqueID == x);
            if (index != -1) {
                tripLegData.push({
                    uniqueID: records[index].uniqueID,
                    type: records[index].recordType
                });
            }
        })
        const obj = {
            clientId: client,
            CobdenialInvoiceSId: invoiceInfo.id,
            importedTripLegs: tripLegData
        }
        return obj;
    }

    const importLegs = () => {
        confirm({
            title: 'Do you want to import selected trips?',
            cancelText: "No",
            okText: "Yes",
            onOk() {
                setLegsLoader(true);
                saveCobDenialImportTrips(getImportObj(), (res: any) => {
                    setLegsLoader(false);
                    if (res?.hasError) {
                        const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!';
                        parentCallback({
                            isInvalid: true,
                            errorMessage: errorMsg
                        })
                    } else {
                        parentCallback({
                            success: true,
                            showMessage: true,
                            message: 'Trip Legs imported successfully!'
                        });
                        setSelectedTripLegs([]);
                        setRecords([]);
                        handleSearch();
                    }
                })
            },
            onCancel() { },
        });

        
    }

    const cancelImport = () => {
        setSelectedTripLegs([]);
        setTableKey((prevKey) => prevKey + 1);
    }
    
    const searchTypeChanged = (e: any) => {
        setSearchByType(e);
        setSearchText('');
        setStartDate('');
        setEndDate('');     
    }

    return (
        <>
            <div className={records?.length > 0 ? 'parentDiv' : ''}>
                <div style={{ width: '100%' }}>
                    <div style={{ background: "#fff", padding: '15px' }}>
                        <Row style={{
                            paddingBottom: '0.5rem', paddingTop: "0.5rem"
                        }}>
                            <Col span={24}>
                                <div style={{ width: "100%" }}>
                                    <Select
                                        style={{ width: "250px" }}
                                        onChange={(value) => searchTypeChanged(value)}
                                        disabled={selectedTripLegs?.length > 0}
                                        value={searchByType} >
                                        {searchTypeOptions && searchTypeOptions.map((item: any) => (
                                            <Option key={item.value}>{item.name}</Option>
                                        ))}
                                    </Select>
                                    {
                                        searchByType != 1 && 
                                        <Input
                                            style={{ width: 'calc(100% - 250px)' }}
                                            allowClear
                                            status={submitted && !searchText ? 'error' : ''}
                                            placeholder={"Enter Comma separated value" + (searchTypeOptions?.length && searchByType ? " of " + searchTypeOptions.find((x) => x.value == searchByType)?.name : '')}
                                            value={searchText}
                                            onChange={handleSearchTextChange}
                                            disabled={selectedTripLegs?.length > 0}
                                        />
                                    }
                                    {
                                        searchByType == 1 &&
                                        
                                        <RangePicker
                                            style={{ width: 'calc(100% - 250px)' }}
                                            allowClear
                                            status={submitted && !startDate && !endDate ? 'error' : ''}
                                            name="SearchDateRangePicker"
                                            placeholder={[ "From Date", "To Date" ]}
                                            format="MM/DD/YYYY"
                                            onChange={(dates: any) => {
                                                if (dates) {
                                                    setStartDate(dates[ 0 ]);
                                                    setEndDate(dates[ 1 ]);
                                                } else {
                                                    setStartDate('');
                                                    setEndDate('');
                                                }
                                            }}
                                            disabled={selectedTripLegs?.length > 0}
                                      
                                      />
                                    }
                                    
                                </div>
                            </Col>
                        </Row>
                        <Flex justify='end' style={{ paddingBottom: "0.5rem", paddingTop: "0.5rem", alignItems: "center", background: '#fff' }} gap='small'>
                            <Button onClick={handleSearch} loading={tablePagination.loading} style={{ marginLeft: '5px' }} type="primary" disabled={selectedTripLegs?.length > 0}>
                                Search
                            </Button>
                        </Flex>
                    </div>
                    <Card styles={{ body: { padding: '15px', paddingTop: '10px', height: "auto", maxHeight: records?.length > 0 ? "calc(100vh - 268px)" : undefined } }}>
                        {
                            records?.length > 0 &&
                            <Flex justify='start' style={{ paddingBottom: "5px", paddingTop: "0", alignItems: "center", background: '#fff' }} gap='small'>
                                <div style={{ background: '#e6f4ff', padding: '0px, 5px', fontWeight: 500 }}>
                                    <span style={{ marginRight: '5px', marginLeft: '5px', borderRight: "1px solid #d4d2d2" , paddingRight: "5px" }}>
                                        Total: {records.length}
                                    </span>
                                    <span style={{ marginRight: '5px' }}>
                                        Selected: {selectedTripLegs.length}
                                    </span>
                                    {
                                        filteredRowCount > 0 && filteredRowCount != records.length &&
                                        <span style={{ marginRight: '5px', borderLeft: "1px solid #d4d2d2", paddingLeft: "5px" }}>
                                            Filtered: {filteredRowCount}
                                        </span>
                                    }
                                </div>
                            </Flex>
                        }
                        {
                            records?.length > 0 &&
                            <div id="table-container" style={{ height: "calc(100vh - 360px)", width: "100%", overflowY: "hidden", overflowX: "auto" }}>
                                <Table
                                    scroll={{
                                        y: tableHeight,
                                        x: tableColumns.reduce((acc, col:any) => acc + (col.width || 150), 0) + 25,
                                    }}
                                    key={tableKey}
                                    ref={tblRef}
                                    virtual={true}
                                    className={'bulkUpdateView'}
                                    rowSelection={rowSelection}
                                    dataSource={records}
                                    pagination={false}
                                    columns={tableColumns}
                                    onChange={tableChanges}
                                    rowKey="uniqueID"
                                    size="small"
                                    loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: tablePagination.loading }}
                                    onHeaderRow={(columns, index) => {
                                        return {
                                            onContextMenu: (event) => {
                                                event.preventDefault();
                                                if (!menuVisible) {
                                                    document.addEventListener(`click`, function onClickOutside() {
                                                        setMenuVisible(false);
                                                        document.removeEventListener(`click`, onClickOutside);
                                                    });
                                                }
                                                setMenuVisible(true);
                                            }
                                        };
                                    }}
                                />
                            </div>
                        }
                        {
                            records?.length > 0 &&
                            <Flex justify='end' style={{ paddingTop: "1rem", background: '#fff' }} gap='small'>
                                <Button key="saveBulk" loading={legsLoader} onClick={importLegs} style={{ background: '#1677ff', color: '#fff' }}>
                                    Import
                                </Button>
                                <Button key="cancelImport" onClick={cancelImport} style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: 'rgba(0, 0, 0, 0.88)' }}>
                                    Cancel
                                </Button>
                            </Flex>
                        }
                    </Card>
                </div>
            </div>
        </>
    );
};